import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Search, 
  Filter, 
  FileText,
  ChevronRight,
  Presentation,
  X
} from 'lucide-react';

const STATUS_STYLES = {
  'Draft': 'status-badge status-draft',
  'For validation': 'status-badge status-validation',
  'Validated': 'status-badge status-validated',
  'Deferred': 'status-badge status-deferred',
  'Archived': 'status-badge status-archived'
};

const DecisionLibraryPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [decisions, setDecisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    twg_view: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchDecisions();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDecisions();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await api().get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchDecisions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.twg_view) params.append('twg_view', filters.twg_view);

      const response = await api().get(`/decisions?${params.toString()}`);
      setDecisions(response.data);
    } catch (error) {
      console.error('Failed to fetch decisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      status: '',
      twg_view: ''
    });
  };

  const hasActiveFilters = (filters.category && filters.category !== "all") || 
                           (filters.status && filters.status !== "all") || 
                           (filters.twg_view && filters.twg_view !== "all");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="decision-library">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-serif">Decision Library</h1>
          <p className="text-muted-foreground mt-1">
            Browse and manage NSR data collection strategy decisions
          </p>
        </div>
        <Button 
          onClick={() => navigate('/meeting-mode')}
          className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
          data-testid="meeting-mode-btn"
        >
          <Presentation className="w-4 h-4 mr-2" />
          Meeting Mode
        </Button>
      </div>

      {/* Filters */}
      <Card className="paper-card">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search decisions by ID, question, or statement..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Category Filter */}
            <Select
              value={filters.category || "all"}
              onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-full md:w-[200px]" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="For validation">For validation</SelectItem>
                <SelectItem value="Validated">Validated</SelectItem>
                <SelectItem value="Deferred">Deferred</SelectItem>
                <SelectItem value="Archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            {/* TWG Filter */}
            <Select
              value={filters.twg_view || "all"}
              onValueChange={(value) => setFilters({ ...filters, twg_view: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-full md:w-[150px]" data-testid="twg-filter">
                <SelectValue placeholder="TWG View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Yes">TWG Yes</SelectItem>
                <SelectItem value="No">TWG No</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <FileText className="w-4 h-4" />
        <span>{decisions.length} decisions found</span>
      </div>

      {/* Decisions Table */}
      <Card className="paper-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : decisions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <FileText className="w-12 h-12 mb-4 opacity-50" />
            <p>No decisions found</p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <Table data-testid="decisions-table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[100px]">ID</TableHead>
                <TableHead>Decision Question</TableHead>
                <TableHead className="w-[150px]">Category</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[80px]">TWG</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {decisions.map((decision) => (
                <TableRow 
                  key={decision.decision_id}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => navigate(`/decisions/${decision.decision_id}`)}
                  data-testid={`decision-row-${decision.decision_id}`}
                >
                  <TableCell className="font-mono font-medium text-primary">
                    {decision.decision_id}
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="line-clamp-2">{decision.decision_question || 'No question'}</p>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">
                      {decision.category?.replace(/\s*\([^)]*\)/g, '')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={STATUS_STYLES[decision.status] || 'status-badge status-draft'}>
                      {decision.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {decision.twg_view === 'Yes' && (
                      <Badge variant="outline" className="text-xs">Yes</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default DecisionLibraryPage;
